import React, { useCallback, useEffect, useState } from 'react';
import api from '../api/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Star, ChatCircleDots, PencilSimple, Trash } from '@phosphor-icons/react';
import { toast } from 'sonner';

const Stars = ({ value, size = 16 }) => (
  <div className="flex">
    {[...Array(5)].map((_, i) => (
      <Star key={i} size={size} weight={i < Math.round(value) ? 'fill' : 'regular'} className={i < Math.round(value) ? 'text-accent' : 'text-slate-300'} />
    ))}
  </div>
);

const ReplyBox = ({ review, onSaved }) => {
  const [editing, setEditing] = useState(!review.reply);
  const [text, setText] = useState(review.reply?.text || '');
  const [saving, setSaving] = useState(false);
  const id = review.review_id;
  const save = async () => {
    if (!text.trim()) return toast.error('Scrivi una risposta');
    setSaving(true);
    try { await api.put(`/api/reviews/${id}/reply`, { text }); toast.success('Risposta pubblicata'); setEditing(false); onSaved(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Pubblicazione non riuscita'); }
    finally { setSaving(false); }
  };
  const remove = async () => {
    if (!window.confirm('Eliminare la risposta?')) return;
    try { await api.delete(`/api/reviews/${id}/reply`); toast.success('Risposta eliminata'); setText(''); setEditing(true); onSaved(); }
    catch { toast.error('Eliminazione non riuscita'); }
  };
  if (!editing && review.reply) {
    return (
      <div className="rounded-xl bg-[#F5FAFF] border-l-4 border-primary p-3 space-y-1" data-testid={`review-reply-${id}`}>
        <div className="text-xs font-semibold text-primary">Risposta di {review.reply.author_name}</div>
        <p className="text-sm text-slate-700 whitespace-pre-line">{review.reply.text}</p>
        <div className="flex gap-1">
          <Button variant="ghost" size="sm" onClick={() => setEditing(true)} data-testid={`review-reply-edit-${id}`}><PencilSimple size={14} className="mr-1" />Modifica</Button>
          <Button variant="ghost" size="sm" className="text-red-600" onClick={remove} data-testid={`review-reply-delete-${id}`}><Trash size={14} className="mr-1" />Elimina</Button>
        </div>
      </div>
    );
  }
  return (
    <div className="space-y-2">
      <Textarea rows={2} maxLength={1000} value={text} onChange={(e) => setText(e.target.value)} placeholder="Ringrazia il cliente o chiarisci con garbo: la risposta è pubblica" data-testid={`review-reply-input-${id}`} />
      <div className="flex gap-2">
        <Button size="sm" className="rounded-full" onClick={save} disabled={saving} data-testid={`review-reply-save-${id}`}>{saving ? 'Pubblicazione…' : 'Pubblica risposta'}</Button>
        {review.reply && <Button size="sm" variant="ghost" onClick={() => { setText(review.reply.text); setEditing(false); }} data-testid={`review-reply-cancel-${id}`}>Annulla</Button>}
      </div>
    </div>
  );
};

const ReviewCard = ({ r, showPro, onSaved }) => (
  <Card className="p-5 space-y-3" data-testid={`review-card-${r.review_id}`}>
    <div className="flex flex-wrap items-center gap-3">
      <Stars value={r.rating} />
      <span className="font-medium">{r.client_name}</span>
      {showPro && r.professional_name && <span className="text-xs px-2 py-0.5 rounded-full bg-sky-50 text-sky-800">per {r.professional_name}</span>}
      <span className="text-xs text-slate-500 ml-auto">{new Date(r.created_at).toLocaleDateString('it-IT')}</span>
    </div>
    {r.comment ? <p className="text-slate-700">{r.comment}</p> : <p className="text-sm text-slate-400 italic">Nessun commento, solo voto</p>}
    <ReplyBox review={r} onSaved={onSaved} />
  </Card>
);

export default function ReviewsPage() {
  const [data, setData] = useState({ items: [], count: 0, average: 0, unanswered: 0 });
  const [onlyUnanswered, setOnlyUnanswered] = useState(false);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    try { const r = await api.get('/api/reviews/mine'); setData(r.data); }
    catch (e) { toast.error(e.response?.data?.detail || 'Errore nel caricamento delle recensioni'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const shown = onlyUnanswered ? data.items.filter((r) => !r.reply) : data.items;

  return (
    <div className="p-4 md:p-8 max-w-4xl space-y-6" data-testid="reviews-page">
      <div className="flex flex-wrap items-end gap-6">
        <div className="flex-1 min-w-[220px]">
          <h2 className="font-heading text-2xl font-semibold">Recensioni</h2>
          <p className="text-sm text-slate-500">Leggi cosa dicono i clienti e rispondi pubblicamente.</p>
        </div>
        <div className="flex items-center gap-3 bg-white rounded-2xl border border-[#E3F2FD] px-4 py-3" data-testid="reviews-average">
          <span className="text-3xl font-heading font-bold">{data.average || '–'}</span>
          <div><Stars value={data.average} /><div className="text-xs text-slate-500">{data.count} recensioni</div></div>
        </div>
      </div>
      <div className="flex gap-2">
        <button onClick={() => setOnlyUnanswered(false)} data-testid="reviews-filter-all"
          className={`px-3 py-1.5 rounded-full text-sm transition-colors ${!onlyUnanswered ? 'bg-primary text-white' : 'bg-white border text-slate-600 hover:border-primary'}`}>Tutte ({data.count})</button>
        <button onClick={() => setOnlyUnanswered(true)} data-testid="reviews-filter-unanswered"
          className={`px-3 py-1.5 rounded-full text-sm transition-colors ${onlyUnanswered ? 'bg-primary text-white' : 'bg-white border text-slate-600 hover:border-primary'}`}>Senza risposta ({data.unanswered})</button>
      </div>
      {loading ? <p className="text-sm text-slate-500">Caricamento…</p> : shown.length === 0 ? (
        <Card className="p-8 text-center text-slate-500 flex flex-col items-center gap-2" data-testid="reviews-empty">
          <ChatCircleDots size={28} className="text-primary" />
          {onlyUnanswered ? 'Hai risposto a tutte le recensioni.' : 'Non hai ancora ricevuto recensioni.'}
        </Card>
      ) : shown.map((r) => <ReviewCard key={r.review_id} r={r} showPro={data.is_center} onSaved={load} />)}
    </div>
  );
}
